import {Suspense, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import {css} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {borderRadius, w100} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {fetchAllSearchLists, IFetchAllSearchListsParams, SearchTab} from "../../search/actions/fetch_search_all_action";
import {DesktopBlend} from "../../search/components/atoms/atoms";
import {SearchAutocompleteWithDropdown} from "../../search/components/search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchFormValuesType} from "../../search/types/SearchFormValuesType";
import {fetchRegionsCircles, IMapCircle} from "../api/fetch_regions_circles";
import {defaultCircle} from "../constants/default_circle";
import {searchFormActions, searchFormFields} from "../reducers/property_configurator_reducer";
import {PropertyConfiguratorStep} from "../views/PropertyConfigurator";
import {ActionButtons} from "./ActionButtons";

interface IOwnProps {
    handleTabChange: (value: PropertyConfiguratorStep, prev?: boolean) => void;
}

interface IStateProps {
    formValues: SearchFormValuesType;
    initialValues: SearchFormValuesType;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const getRadiusWithDistance = (distance: number | string, value: number) => {
    // TODO check and fix why default distance is not a type number and value 5, but empty string
    return value + (typeof distance === "number" ? distance : 5) * 1000;
};
export const LocationTabC = (props: IProps) => {
    const regionsIds: number[] = useMemo(
        () => props.formValues?.search?.regions?.map(({id}: {id: number}) => id) || [],
        [props.formValues?.search?.regions?.length]
    );
    const {isMobile} = useUserDevice();
    const [showLocationSearch, setShowLocationSearch] = useState(false);
    const [mapCircles, setMapCircles] = useState<IMapCircle[]>([]);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);
    const [blendTopDistance, setBlendTopDistance] = useState(0);

    const fetchRegions = async () => setMapCircles(await fetchRegionsCircles(regionsIds));

    useEffect(() => {
        fetchRegions();
    }, [regionsIds]);

    useEffect(() => {
        if (!isMobile) {
            setShowLocationSearch(false);
        }
    }, [isMobile]);

    const circles: IMapCircle[] = useMemo(() => {
        if (!!mapCircles.length) {
            return mapCircles.map((circle) => ({
                ...circle,
                radius: getRadiusWithDistance(props.formValues.distance, circle.radius)
            }));
        }
        return defaultCircle;
    }, [mapCircles, props.formValues.distance]);
    const calcBlendTopDistance = () => setBlendTopDistance(elementRef.current?.getBoundingClientRect().y as number);

    useEffect(() => {
        calcBlendTopDistance();
        window.addEventListener("resize", calcBlendTopDistance);

        return () => window.removeEventListener("resize", () => calcBlendTopDistance);
    }, []);

    const handleTabChange = (value: PropertyConfiguratorStep, prev?: boolean) => {
        props.handleTabChange(value, prev);
    };

    return (
        <>
            <Text variant="headline_4" mb="1.6rem">
                Lokalizacja
            </Text>
            <Text variant="body_copy_1" mb="3.2rem">
                Wskaż miejsce, gdzie chcesz mieszkać. Im większy promień poszukiwań, tym więcej mieszkań dopasowanych do Twoich oczekiwań.
            </Text>
            <SearchAutocompleteWithDropdown
                onDropdownStatusChange={setDesktopSearchBlendActive}
                formProps={props}
                onSearchSubmit={() => null}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                showSelectedUnderInput={!!isMobile}
                onInputClick={isMobile ? () => setShowLocationSearch(true) : undefined}
                inputDisabled={!!isMobile}
                placeholder="Dodaj lokalizację"
                includeDistanceFilter
            />
            <div ref={elementRef} />
            <DesktopBlend isActive={isDesktopSearchBlendActive} top={`${blendTopDistance - 68}px`} heightDecrease={`${blendTopDistance}px`} />
            {isMobile && showLocationSearch && (
                <MobileOptionsModal modalHeading="Szukaj" isOpen onModalClose={() => setShowLocationSearch(false)}>
                    <SearchAutocompleteWithDropdown
                        onDropdownStatusChange={() => null}
                        formProps={props}
                        onSearchSubmit={() => null}
                        onAfterTabSwitch={() => null}
                        onInputMount={() => null}
                        isInModal
                        includeDistanceFilter={false}
                        placeholder="Dodaj lokalizację"
                    />
                </MobileOptionsModal>
            )}
            <Suspense>
                <LazyOpenStreetMap css={osmStyle} fitBounds circles={circles} />
            </Suspense>
            <ActionButtons
                handleTabChange={handleTabChange}
                nextTab={{
                    label: "Przejdź dalej",
                    value: PropertyConfiguratorStep.ROOMS
                }}
                currentTab={PropertyConfiguratorStep.LOCATION}
            />
        </>
    );
};

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    // fetch search lists for property configurator
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

export const LocationTab = connect(mapStateToProps, mapActionsToProps)(LocationTabC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        initialValues: state.search.formValues
    };
}

const osmStyle = css`
    ${w100};
    min-height: 460px;
    position: initial;
    ${borderRadius(2)};
    overflow: hidden;

    > div {
        min-height: 460px;
    }
`;
import {Suspense, useEffect, useMemo, useRef, useState} from "react";
import {connect} from "react-redux";
import {css} from "@emotion/react";
import {bindActionCreators, Dispatch} from "redux";

import {borderRadius, w100} from "@pg-design/helpers-css";
import {Text} from "@pg-design/text-module";
import {FormActions} from "@pg-mono/form";
import {LazyOpenStreetMap} from "@pg-mono/open-street-map";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../app/rp_reducer";
import {MobileOptionsModal} from "../../atoms/MobileOptionsModal";
import {fetchAllSearchLists, IFetchAllSearchListsParams, SearchTab} from "../../search/actions/fetch_search_all_action";
import {DesktopBlend} from "../../search/components/atoms/atoms";
import {SearchAutocompleteWithDropdown} from "../../search/components/search_autocomplete/SearchAutocompleteWithDropdown";
import {SearchFormValuesType} from "../../search/types/SearchFormValuesType";
import {fetchRegionsCircles, IMapCircle} from "../api/fetch_regions_circles";
import {defaultCircle} from "../constants/default_circle";
import {searchFormActions, searchFormFields} from "../reducers/property_configurator_reducer";
import {PropertyConfiguratorStep} from "../views/PropertyConfigurator";
import {ActionButtons} from "./ActionButtons";

interface IOwnProps {
    handleTabChange: (value: PropertyConfiguratorStep, prev?: boolean) => void;
}

interface IStateProps {
    formValues: SearchFormValuesType;
    initialValues: SearchFormValuesType;
}
interface IActionsProps {
    formActions: FormActions<Record<keyof typeof searchFormFields, unknown>>;
    fetchAllSearchLists: (searchInput: string, params: IFetchAllSearchListsParams) => Promise<SearchTab>;
}

interface IProps extends IStateProps, IActionsProps, IOwnProps {}

const getRadiusWithDistance = (distance: number | string, value: number) => {
    // TODO check and fix why default distance is not a type number and value 5, but empty string
    return value + (typeof distance === "number" ? distance : 5) * 1000;
};
export const LocationTabC = (props: IProps) => {
    const regionsIds: number[] = useMemo(
        () => props.formValues?.search?.regions?.map(({id}: {id: number}) => id) || [],
        [props.formValues?.search?.regions?.length]
    );
    const {isMobile} = useUserDevice();
    const [showLocationSearch, setShowLocationSearch] = useState(false);
    const [mapCircles, setMapCircles] = useState<IMapCircle[]>([]);
    const [isDesktopSearchBlendActive, setDesktopSearchBlendActive] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);
    const [blendTopDistance, setBlendTopDistance] = useState(0);

    const fetchRegions = async () => setMapCircles(await fetchRegionsCircles(regionsIds));

    useEffect(() => {
        fetchRegions();
    }, [regionsIds]);

    useEffect(() => {
        if (!isMobile) {
            setShowLocationSearch(false);
        }
    }, [isMobile]);

    const circles: IMapCircle[] = useMemo(() => {
        if (!!mapCircles.length) {
            return mapCircles.map((circle) => ({
                ...circle,
                radius: getRadiusWithDistance(props.formValues.distance, circle.radius)
            }));
        }
        return defaultCircle;
    }, [mapCircles, props.formValues.distance]);
    const calcBlendTopDistance = () => setBlendTopDistance(elementRef.current?.getBoundingClientRect().y as number);

    useEffect(() => {
        calcBlendTopDistance();
        window.addEventListener("resize", calcBlendTopDistance);

        return () => window.removeEventListener("resize", () => calcBlendTopDistance);
    }, []);

    const handleTabChange = (value: PropertyConfiguratorStep, prev?: boolean) => {
        props.handleTabChange(value, prev);
    };

    return (
        <>
            <Text variant="headline_4" mb="1.6rem">
                Lokalizacja
            </Text>
            <Text variant="body_copy_1" mb="3.2rem">
                Wskaż miejsce, gdzie chcesz mieszkać. Im większy promień poszukiwań, tym więcej mieszkań dopasowanych do Twoich oczekiwań.
            </Text>
            <SearchAutocompleteWithDropdown
                onDropdownStatusChange={setDesktopSearchBlendActive}
                formProps={props}
                onSearchSubmit={() => null}
                onAfterTabSwitch={() => null}
                onInputMount={() => null}
                showSelectedUnderInput={!!isMobile}
                onInputClick={isMobile ? () => setShowLocationSearch(true) : undefined}
                inputDisabled={!!isMobile}
                placeholder="Dodaj lokalizację"
                includeDistanceFilter
            />
            <div ref={elementRef} />
            <DesktopBlend isActive={isDesktopSearchBlendActive} top={`${blendTopDistance - 68}px`} heightDecrease={`${blendTopDistance}px`} />
            {isMobile && showLocationSearch && (
                <MobileOptionsModal modalHeading="Szukaj" isOpen onModalClose={() => setShowLocationSearch(false)}>
                    <SearchAutocompleteWithDropdown
                        onDropdownStatusChange={() => null}
                        formProps={props}
                        onSearchSubmit={() => null}
                        onAfterTabSwitch={() => null}
                        onInputMount={() => null}
                        isInModal
                        includeDistanceFilter={false}
                        placeholder="Dodaj lokalizację"
                    />
                </MobileOptionsModal>
            )}
            <Suspense>
                <LazyOpenStreetMap css={osmStyle} fitBounds circles={circles} />
            </Suspense>
            <ActionButtons
                handleTabChange={handleTabChange}
                nextTab={{
                    label: "Przejdź dalej",
                    value: PropertyConfiguratorStep.ROOMS
                }}
                currentTab={PropertyConfiguratorStep.LOCATION}
            />
        </>
    );
};

function mapActionsToProps(dispatch: Dispatch): IActionsProps {
    // fetch search lists for property configurator
    const searchContentParams = {
        googlePOIs: false,
        regions: true,
        offers: false,
        vendors: false
    };
    return {
        formActions: bindActionCreators({...searchFormActions}, dispatch),
        ...bindActionCreators({fetchAllSearchLists: (searchInput: string) => fetchAllSearchLists(searchInput, {searchContent: searchContentParams})}, dispatch)
    };
}

export const LocationTab = connect(mapStateToProps, mapActionsToProps)(LocationTabC);

function mapStateToProps(state: IRPStore): IStateProps {
    return {
        formValues: state.propertyConfigurator.formValues,
        initialValues: state.search.formValues
    };
}

const osmStyle = css`
    ${w100};
    min-height: 460px;
    position: initial;
    ${borderRadius(2)};
    overflow: hidden;

    > div {
        min-height: 460px;
    }
`;
